import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Check, Loader2, Package, Phone } from "lucide-react";
import api from "../lib/api";
import { useSiteContent } from "../contexts/SiteContentContext";

export default function PackagesTab() {
  const { text } = useSiteContent();
  const t = text.packages || {};
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/packages/public")
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Nepodařilo se načíst balíčky."))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-apex-gold" />
      </div>
    );
  }
  if (err) {
    return <div className="text-red-300 text-sm">{err}</div>;
  }
  if (!data || !data.packages?.length) {
    return <div className="text-apex-cream/60 text-sm">Aktuálně nejsou k dispozici žádné balíčky.</div>;
  }

  const { packages, phone, phoneRaw } = data;
  const ctaLabel = t.phone_cta_label || data.ctaLabel || "Zavolejte nám";

  return (
    <div data-testid="packages-tab">
      <div className="mb-6">
        <h3 className="font-display text-2xl text-apex-cream font-bold flex items-center gap-2">
          <Package className="w-6 h-6 text-apex-gold" />
          {t.title || "Výhodné balíčky"}
        </h3>
        <p className="text-apex-cream/65 text-sm mt-2 max-w-2xl whitespace-pre-line">
          {t.subtitle || "Sestavili jsme balíčky pro řidiče, kteří chtějí mít auto pořád v perfektní kondici."}
        </p>
        {phone && (
          <div className="mt-5">
            <a
              href={`tel:${phoneRaw || phone}`}
              className="phone-cta"
              data-testid="packages-phone-cta"
            >
              <Phone className="w-4 h-4 flex-shrink-0" />
              <span className="flex flex-col sm:flex-row sm:items-center gap-0.5 sm:gap-1.5 text-left leading-tight">
                <span>{ctaLabel}</span>
                <span className="font-semibold tracking-wide">{phone}</span>
              </span>
            </a>
          </div>
        )}
      </div>

      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {packages.map((p, i) => (
          <PackageCard key={p.id} pkg={p} index={i} />
        ))}
      </div>

      <p className="mt-6 text-xs text-apex-cream/45 leading-relaxed whitespace-pre-line">
        {t.footer_note || "* Uvedené ceny jsou orientační."}
      </p>
    </div>
  );
}

function PackageCard({ pkg, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.08 }}
      className={`relative rounded-2xl border p-5 flex flex-col text-apex-cream transition-colors ${
        pkg.popular
          ? "bg-apex-card border-apex-gold/60 shadow-[0_0_30px_-12px_rgba(229,200,128,0.45)]"
          : "bg-apex-card border-apex-line hover:border-apex-gold/40"
      }`}
      data-testid={`package-card-${pkg.id}`}
    >
      {pkg.popular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-apex-gold text-apex-bg text-[10px] font-bold uppercase tracking-[0.2em] whitespace-nowrap" data-testid={`package-popular-${pkg.id}`}>
          Nejoblíbenější
        </div>
      )}
      <div className="text-[10px] uppercase tracking-[0.3em] mb-1 text-apex-cream/55">{pkg.frequency}</div>
      <h4 className="font-display text-xl font-bold mb-1">{pkg.name}</h4>
      <div className="text-sm mb-2 text-apex-cream/75">{pkg.tagline}</div>
      <div className="text-xs mb-4 text-apex-cream/55">{pkg.subtitle}</div>

      <div className="text-2xl font-display font-bold mb-4 text-apex-gold">
        {pkg.priceLabel}
      </div>

      <ul className="space-y-2 text-sm">
        {pkg.features.map((f, i) => (
          <li key={i} className="flex items-start gap-2">
            <Check className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-apex-gold" />
            <span className="text-apex-cream/85">{f}</span>
          </li>
        ))}
      </ul>
    </motion.div>
  );
}
